
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Asset, AssetCategory, Supplier, CostCenter, Manufacturer, AssetStatus } from '../types';
import { api } from '../services/api';
import { AssetDetailPanel } from './AssetDetailPanel';
import { AssetImportModal } from './import/AssetImportModal';
import {
    Plus, Loader2, Search, Filter, ChevronLeft, ChevronRight, Check, X,
    Smartphone, FileText, Tag, MapPin, Euro, Image as ImageIcon, Briefcase, User as UserIcon, Calendar, Barcode, Download, Upload, AlertTriangle, Trash2, RotateCcw, Printer
} from 'lucide-react';

import { LocalizedDatePicker } from './ui/LocalizedDatePicker';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency, formatDate } from '../utils/formatters';
import { AssetBulkLabelModal } from './AssetBulkLabelModal';

interface AssetRegistryViewProps {
    user: User;
}

const STEPS = ['identity', 'financials', 'location', 'review'];

const INITIAL_FORM: Omit<Asset, 'id' | 'agencyId' | 'createdAt' | 'updatedAt'> = {
    description: '',
    supplierDescription: '',
    code: '',
    categoryId: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    barcode: '',
    quantity: 1,
    unitValue: 0,
    discountStats: 0,
    totalValue: 0,
    acquisitionDate: new Date().toISOString().split('T')[0],
    residualValue: 0,
    vatPercent: 24,
    supplierId: '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    sentToAccountingDate: '',
    usageStartDate: '',
    images: [],
    documents: [],
    status: 'active', // AssetStatus
    branchId: '',
    costCenterId: '',
    assetManagerId: '',
    assetUserId: '',
    exactLocation: '',
    notes: '',
    color: '',
    height: '',
    length: '',
    width: ''
};

export const AssetRegistryView: React.FC<AssetRegistryViewProps> = ({ user }) => {
    const { t } = useTranslation('assets');
    const [viewMode, setViewMode] = useState<'list' | 'wizard'>('list');
    const [assets, setAssets] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [metaLoading, setMetaLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [editAsset, setEditAsset] = useState<Asset | null>(null);

    const isAssetManager = user.memberProfile?.teamRole === 'asset_manager';

    const [categories, setCategories] = useState<AssetCategory[]>([]);
    const [branches, setBranches] = useState<User[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [team, setTeam] = useState<User[]>([]);
    const [showImport, setShowImport] = useState(false);

    // Detail View State
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    const toggleGroup = (groupId: string) => {
        const newSet = new Set(expandedGroups);
        if (newSet.has(groupId)) newSet.delete(groupId);
        else newSet.add(groupId);
        setExpandedGroups(newSet);
    };

    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [assetToDelete, setAssetToDelete] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteAsset = (id: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setAssetToDelete(id);
    };

    const confirmDeletion = async () => {
        if (!assetToDelete) return;
        setIsDeleting(true);
        try {
            await api.deleteAsset(assetToDelete);
            loadAssets();
            if (selectedAsset?.id === assetToDelete) setSelectedAsset(null);
            setAssetToDelete(null);
        } catch (error: any) {
            console.error("Delete failed", error);
            // Check for specific API error message to localize
            if (error.message && error.message.includes("Cannot delete Master Asset")) {
                setDeleteError(t('errors.delete_master_with_children'));
            } else {
                setDeleteError(error.message || "Failed to delete asset");
            }
            setAssetToDelete(null); // Close confirm modal, show error modal
        } finally {
            setIsDeleting(false);
        }
    };

    // Bulk Delete State
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [showBulkConfirm, setShowBulkConfirm] = useState(false);
    const [bulkDeleteReport, setBulkDeleteReport] = useState<string | null>(null);
    const [showBulkLabelModal, setShowBulkLabelModal] = useState(false);
    const [printAssets, setPrintAssets] = useState<Asset[]>([]);

    const toggleSelect = (id: string) => {
        const newSet = new Set(selectedIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedIds(newSet);
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === assets.length && assets.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(assets.map(a => a.id)));
        }
    };

    const [agencyName, setAgencyName] = useState<string>('');

    const confirmBulkDeletion = async () => {
        setIsDeleting(true);
        let successCount = 0;
        let failCount = 0;
        const failedMasters = [];

        try {
            const ids = Array.from(selectedIds);
            // Process sequentially to be safe with rate limits or dependencies
            for (const id of ids) {
                try {
                    await api.deleteAsset(id);
                    successCount++;
                } catch (error: any) {
                    failCount++;
                    if (error.message && error.message.includes("Cannot delete Master")) {
                        failedMasters.push(id);
                    }
                }
            }
            loadAssets();
            setSelectedIds(new Set());
            setShowBulkConfirm(false);

            if (failCount > 0) {
                setDeleteError(`Deleted ${successCount} assets. Failed to delete ${failCount} assets. ${failedMasters.length > 0 ? "Some were Master Assets with children." : ""}`);
            }
        } catch (error) {
            console.error("Bulk delete failed", error);
            setDeleteError("Critical error during bulk deletion.");
        } finally {
            setIsDeleting(false);
        }
    };

    // Grouping Logic
    const { groups, standalone } = React.useMemo(() => {
        const _groups: Record<string, { master?: Asset, children: Asset[] }> = {};
        const _standalone: Asset[] = [];

        assets.forEach(asset => {
            const gid = asset.groupId || asset.id;
            // If asset has no masterSerial, it is likely a master
            // If asset has masterSerial, it is a child
            // However, we group by groupId.

            if (!_groups[gid]) _groups[gid] = { children: [] };

            if (!asset.masterSerial) {
                // IS MASTER
                _groups[gid].master = asset;
            } else {
                // IS CHILD
                _groups[gid].children.push(asset);
            }
        });

        // Cleanup: If a group has NO master in this page, treat children as standalone rows (or render them as orphans)
        // Or if a group has master but NO children, it's just a master row.
        // We want to return a list of "Renderable Items".
        // To preserve sort order (created desc), we should ideally iterate 'assets' but skip children that are handled by their master.
        // But 'assets' is mixed.
        // Let's iterate the 'groups' based on the order of appearance of their Master in the original 'assets' list?
        // Or just iterate 'assets' filter masters + orphans.

        return { groups: _groups, standalone: [] };
    }, [assets]);


    // Pagination & Filter State
    const [lastVisible, setLastVisible] = useState<any>(null);
    const [cursors, setCursors] = useState<any[]>([null]); // cursors[0] is always null (Page 1)
    const [hasMore, setHasMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        branchId: user.role === 'user' ? user.id : '',
        costCenterId: '',
        categoryId: '',
        supplierId: '',
        assetManagerId: isAssetManager ? user.id : '',
        assetUserId: ''
    });
    const [filterParentId, setFilterParentId] = useState('');

    useEffect(() => {
        // When user changes (e.g. switching managed client), update branchId filter if needed
        if (user.role === 'user') {
            setFilters(prev => ({ ...prev, branchId: user.id }));
        }

        loadInitialData();
        loadAssets();
    }, [user.id]);

    const loadInitialData = async () => {
        try {
            console.log("[AssetRegistry] Loading initial data. User Role:", user.role, "AgencyId:", user.agencyId);

            if (user.role === 'agency_member' && !user.agencyId) {
                console.warn("[AssetRegistry] Agency Member missing agencyId. Aborting load to prevent permission error.");
                setError("Configuration Error: Your account is missing a valid Agency ID. Please contact support.");
                setMetaLoading(false);
                setLoading(false);
                return;
            }

            const agencyId = user.agencyId || user.id;
            console.log("[AssetRegistry] Using Agency ID:", agencyId);

            // Fetch Data
            // If Asset Manager, only fetch assigned clients to avoid permission error on 'list all'
            let allAgencyUsers: User[] = [];

            allAgencyUsers = await api.getAgencyClients(agencyId, isAssetManager ? user.id : undefined, true);

            const [cats, supps, ccenters, profile] = await Promise.all([
                api.getAssetCategories(agencyId),
                api.getSuppliers(agencyId),
                api.getCostCenters(agencyId),
                api.getPublicProfile(agencyId).catch(() => ({ companyName: '' }))
            ]);
            setCategories(cats);
            setSuppliers(supps);
            setCostCenters(ccenters);
            if (profile && profile.companyName) {
                setAgencyName(profile.companyName);
            }

            let _branches = allAgencyUsers.filter(u => u.role !== 'agency_member' && u.role !== 'agency');
            // If Asset Manager, we might have received only Clients. We need to ensure WE are in the team list.
            const _team = allAgencyUsers.filter(u => u.role === 'agency_member' || u.role === 'agency');

            // Asset Manager Constraint: Only see assigned branches (Already filtered by API query above effectively, but keep filter for safety)
            if (isAssetManager) {
                console.log("[AssetRegistry] Applying Asset Manager restrictions.");
                // Ensure current user is in team list for the filter dropdown
                if (!_team.find(u => u.id === user.id)) {
                    _team.push(user);
                }
                // Pre-select and Lock Filter
                setFilters(prev => ({ ...prev, assetManagerId: user.id }));
            }

            if (user.role === 'agency' && !_team.find(u => u.id === user.id)) _team.push(user);

            setBranches(_branches);
            setTeam(_team);
        } catch (e: any) {
            console.error("Load Initial Data Failed:", e);
            setError("Failed to load configuration data: " + e.message);
        } finally {
            setMetaLoading(false);
        }
    };

    // --- DELETE ALL ASSETS (Agency Only) ---
    const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
    const [isDeletingAll, setIsDeletingAll] = useState(false);

    const handleDeleteAll = async () => {
        setIsDeletingAll(true);
        try {
            const agencyId = user.agencyId || user.id;
            await api.deleteAllAssets(agencyId);
            loadAssets();
            setShowDeleteAllConfirm(false);
            // Show success toast or alert?
        } catch (err) {
            console.error("Delete All Failed", err);
            // Handle error
        } finally {
            setIsDeletingAll(false);
        }
    };

    const loadAssets = async (startAfter: any = null) => {
        setLoading(true);
        // Clear selection on new data load (page change or filter)
        setSelectedIds(new Set());
        try {
            if (user.role === 'agency_member' && !user.agencyId) {
                console.warn("[AssetRegistry] Agency Member missing agencyId. Aborting loadAssets.");
                setLoading(false);
                return;
            }
            const agencyId = user.agencyId || user.id;

            // Handle Category Hierarchy Filter
            let effectiveFilters: any = { ...filters };
            if (filterParentId && filters.categoryId === filterParentId) {
                // Parent selected, no subcategory specific selection
                // Include Parent ID and ALL its children IDs
                const childIds = categories.filter(c => c.parentId === filterParentId).map(c => c.id);
                const allIds = [filterParentId, ...childIds];
                effectiveFilters.categoryIds = allIds;
                // Note: 'categoryId' in filters remains set to parentId, but 'categoryIds' takes precedence in API logic
            }

            // Limit 100 per page as requested
            const res = await api.getAssets(agencyId, startAfter, 300, effectiveFilters);
            console.log(`[View Debug] Loaded ${res.data.length} assets. Total: ${res.totalCount}`);
            // Check for the specific parent code
            const parent = res.data.find(a => a.code === '202300015');
            const children = res.data.filter(a => a.code?.startsWith('202300015-'));
            console.log(`[View Debug] Parent '202300015' found?`, parent ? `YES (ID: ${parent.id}, Grp: ${parent.groupId}, Master: ${parent.masterSerial})` : 'NO');
            console.log(`[View Debug] Children found: ${children.length}`, children.map(c => `${c.code} (Grp: ${c.groupId}, Master: ${c.masterSerial})`));

            setAssets(res.data);
            setLastVisible(res.lastVisible);
            setHasMore(res.hasMore);
            setTotalCount(res.totalCount || 0);
        } catch (error: any) {
            console.error(error);
            // Handle Index Building Error
            if (error?.message?.includes('index') || error?.code === 'failed-precondition') {
                setError("System is currently optimizing the database indexes for this view. This may take a few minutes. Please check back shortly.");
            } else {
                setError("Failed to load assets. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key: string, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        // Reset pagination on filter change
        setCurrentPage(1);
        setCursors([null]);
        setLastVisible(null);
    };

    // Trigger reload when filters change (debounce)
    useEffect(() => {
        const timeout = setTimeout(() => {
            loadAssets(null);
        }, 300);
        return () => clearTimeout(timeout);
    }, [filters]);

    // Pagination Handlers
    const goToPage = (page: number) => {
        if (page < 1 || page > cursors.length) return;
        const cursor = cursors[page - 1];
        loadAssets(cursor);
        setCurrentPage(page);
    };

    const onNext = () => {
        if (!hasMore) return;

        // We need to load the next page using the LAST VISIBLE cursor from the *current* page.
        // We also need to update the cursor stack so 'Prev' works.
        const nextPage = currentPage + 1;
        const newCursors = [...cursors];

        // Save the cursor that starts page 'nextPage' (which is 'lastVisible' from current page)
        // Note: cursors[0] is null (Page 1 start). cursors[1] is Page 2 start.
        newCursors[currentPage] = lastVisible;

        setCursors(newCursors);
        setCurrentPage(nextPage);

        // DIRECTLY load using the known cursor, don't wait for 'cursors' state update
        loadAssets(lastVisible);
    };

    const onPrev = () => {
        if (currentPage > 1) {
            goToPage(currentPage - 1);
        }
    };

    const getCategoryInfo = (id: string) => {
        const cat = categories.find(c => c.id === id);
        if (!cat) return { title: id };
        if (cat.parentId) {
            const parent = categories.find(p => p.id === cat.parentId);
            return parent
                ? { title: parent.title, subTitle: cat.title }
                : { title: cat.title };
        }
        return { title: cat.title };
    };
    const getBranchName = (id: string) => {
        const b = branches.find(b => b.id === id);
        return b ? (b.profile?.companyName || b.config?.companyName || b.displayName || b.email) : id;
    };
    const getCostCenterTitle = (id: string) => costCenters.find(c => c.id === id)?.title || id;
    const getSupplierName = (id: string) => suppliers.find(s => s.id === id)?.name || id;
    const getManagerName = (id: string) => {
        const m = team.find(u => u.id === id);
        return m ? (m.memberProfile?.name || m.displayName || m.email) : id;
    };
    const getUserName = (id: string) => {
        // Asset User can be a team member OR a client/branch? Usually team member.
        // Let's search in both team and branches just in case, but usually it's team.
        // Actually, if it's "Employee" assign type.
        const m = team.find(u => u.id === id);
        if (m) return m.memberProfile?.name || m.displayName || m.email;
        const b = branches.find(u => u.id === id);
        return b ? (b.profile?.companyName || b.config?.companyName || b.displayName || b.email) : id;
    };

    const handleExportCSV = async () => {
        try {
            console.log("Exporting CSV with Email Columns...");

            setLoading(true);
            const agencyId = user.agencyId || user.id;

            console.log("Export CSV: Agency ID:", agencyId);
            console.log("Export CSV: Filters:", filters);

            // Fetch assets with current filters
            // Using limit 1000 temporarily to ensure stability.
            // If the user has more, we might need pagination loop, but let's test this first.
            // Handle Category Hierarchy Filter
            let effectiveFilters: any = { ...filters };
            if (filterParentId && filters.categoryId === filterParentId) {
                const childIds = categories.filter(c => c.parentId === filterParentId).map(c => c.id);
                const allIds = [filterParentId, ...childIds];
                effectiveFilters.categoryIds = allIds;
            }

            const res = await api.getAssets(agencyId, null, 1000, effectiveFilters);
            const data = res.data;

            // Sort data by code alphanumerically so that records from a split 
            // (which have sequential codes) appear one below the other.
            data.sort((a, b) => {
                return (a.code || '').localeCompare(b.code || '', undefined, { numeric: true, sensitivity: 'base' });
            });

            console.log("Export fetched records:", data.length);

            // Define Headers
            const headers = [
                'Code', 'Barcode', 'Serial Number', 'Master Serial', 'Description', 'Model', 'Category', 'Subcategory', 'Manufacturer', 'Branch',
                'Department / Cost Center', 'Cost Center Code', 'Location', 'Status', 'Quantity', 'Unit Value',
                'Discount', 'Total Cost', 'VAT', 'Acquisition Date', 'Supplier', 'Supplier Code', 'Supplier VAT No', 'Invoice Number',
                'Invoice Date', 'Date Sent to Accounting', 'Asset Usage Start Date',
                'Asset Manager', 'Asset Manager Email',
                'Asset User', 'Asset User Email', 'Color', 'Height', 'Length', 'Width', 'Notes'
            ];

            // Map Data
            const rows = data.map(asset => {
                const info = getCategoryInfo(asset.categoryId);

                // Helper to get email
                const getManagerEmail = (id: string) => team.find(u => u.id === id)?.email || '';
                const getUserEmail = (id: string) => {
                    const m = team.find(u => u.id === id);
                    if (m) return m.email;
                    const b = branches.find(u => u.id === id);
                    return b?.email || '';
                };

                const getCostCenterName = (id?: string) => {
                    if (!id) return '';
                    return costCenters.find(c => c.id === id)?.title || '';
                };

                const getCostCenterCode = (id?: string) => {
                    if (!id) return '';
                    return costCenters.find(c => c.id === id)?.code || '';
                };

                const getSupplierCode = (id?: string) => {
                    if (!id) return '';
                    return suppliers.find(s => s.id === id)?.code || '';
                };

                const getSupplierVat = (id?: string) => {
                    if (!id) return '';
                    return suppliers.find(s => s.id === id)?.vatNumber || '';
                };

                return [
                    asset.code,
                    asset.barcode || '',
                    asset.serialNumber || '',
                    asset.masterSerial || '',
                    asset.description,
                    asset.model || '',
                    info.title,
                    info.subTitle || '',
                    asset.manufacturer || '',
                    getBranchName(asset.branchId),
                    getCostCenterName(asset.costCenterId),
                    getCostCenterCode(asset.costCenterId),
                    asset.exactLocation || '',
                    t(`status.${asset.status}`),
                    asset.quantity || 1,
                    asset.unitValue || 0,
                    asset.discountStats || 0,
                    asset.totalValue || 0,
                    asset.vatPercent || 0,
                    formatDate(asset.acquisitionDate),
                    getSupplierName(asset.supplierId),
                    getSupplierCode(asset.supplierId),
                    getSupplierVat(asset.supplierId),
                    asset.invoiceNumber || '',
                    formatDate(asset.invoiceDate),
                    formatDate(asset.sentToAccountingDate),
                    formatDate(asset.usageStartDate),
                    getManagerName(asset.assetManagerId),
                    getManagerEmail(asset.assetManagerId),
                    getUserName(asset.assetUserId),
                    getUserEmail(asset.assetUserId),
                    asset.color || '',
                    asset.height || '',
                    asset.length || '',
                    asset.width || '',
                    asset.notes || ''
                ];
            });

            // Convert to CSV
            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(','))
            ].join('\n');

            // Download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `assets_export_full_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error: any) {
            console.error("Export CSV failed", error);
            alert(`Export failed: ${error.message || error}`);
        } finally {
            setLoading(false);
        }
    };

    const handlePrintAllLabels = async () => {
        try {
            setLoading(true);
            const agencyId = user.agencyId || user.id;

            let effectiveFilters: any = { ...filters };
            if (filterParentId && filters.categoryId === filterParentId) {
                const childIds = categories.filter(c => c.parentId === filterParentId).map(c => c.id);
                const allIds = [filterParentId, ...childIds];
                effectiveFilters.categoryIds = allIds;
            }

            let allAssets: Asset[] = [];
            let lastV = null;
            let keepFetching = true;

            while (keepFetching) {
                const res = await api.getAssets(agencyId, lastV, 1000, effectiveFilters);
                if (res.data.length === 0) break;
                allAssets = [...allAssets, ...res.data];
                lastV = res.lastVisible;
                keepFetching = res.hasMore;
            }

            setPrintAssets(allAssets);
            setShowBulkLabelModal(true);
        } catch (error: any) {
            console.error("Fetch all assets for printing failed", error);
            alert(`Failed to fetch assets: ${error.message || error}`);
        } finally {
            setLoading(false);
        }
    };

    const handleExportPDF = async () => {
        try {
            setLoading(true);
            const agencyId = user.agencyId || user.id;

            // Using standard filters object
            // Handle Category Hierarchy Filter
            let effectiveFilters: any = { ...filters };
            if (filterParentId && filters.categoryId === filterParentId) {
                const childIds = categories.filter(c => c.parentId === filterParentId).map(c => c.id);
                const allIds = [filterParentId, ...childIds];
                effectiveFilters.categoryIds = allIds;
            }

            const res = await api.getAssets(agencyId, null, 1000, effectiveFilters);
            const data = res.data;

            if (data.length === 0) {
                // alert(`Debug: No records found for PDF. Agency: ${agencyId}`);
            }

            const doc = new jsPDF();

            // Load Font for Greek Support (Roboto-Regular)
            const fontUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf';
            const fontBytes = await fetch(fontUrl).then(res => res.arrayBuffer());

            // Add font to VFS (Browser safe conversion, no Buffer)
            const fileName = 'Roboto-Regular.ttf';
            const binaryString = Array.from(new Uint8Array(fontBytes)).map(byte => String.fromCharCode(byte)).join("");

            doc.addFileToVFS(fileName, binaryString);
            doc.addFont(fileName, 'Roboto', 'normal');
            doc.setFont('Roboto');

            // Title
            doc.setFontSize(18);
            doc.text("Asset Registry Export", 14, 22);
            doc.setFontSize(11);
            doc.setTextColor(100);
            doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 30);

            // Table
            autoTable(doc, {
                startY: 40,
                head: [['Code', 'Barcode', 'Description', 'Category', 'Subcat', 'Branch', 'Status', 'Value', 'Purchased']],
                body: data.map(asset => {
                    const info = getCategoryInfo(asset.categoryId);
                    return [
                        asset.code,
                        asset.barcode || '',
                        asset.description,
                        info.title,
                        info.subTitle || '',
                        getBranchName(asset.branchId),
                        t(`status.${asset.status}`),
                        formatCurrency(asset.totalValue, user.currency || 'EUR'),
                        formatDate(asset.acquisitionDate)
                    ];
                }),
                styles: {
                    fontSize: 8,
                    font: 'Roboto', // Use the custom font
                    fontStyle: 'normal'
                },
                headStyles: { fillColor: [66, 66, 66] }
            });

            doc.save(`assets_export_${new Date().toISOString().split('T')[0]}.pdf`);

        } catch (error: any) {
            console.error("Export PDF failed", error);
            alert(`Export failed: ${error.message || error}`);
        } finally {
            setLoading(false);
        }
    };

    if (viewMode === 'wizard') {
        return (
            <AssetWizard
                user={user}
                onClose={() => { setViewMode('list'); setEditAsset(null); loadAssets(); }}
                onSuccess={() => { setViewMode('list'); setEditAsset(null); loadInitialData(); loadAssets(); }}
                initialData={editAsset}
            />
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
                <div className="flex gap-2">
                    {/* "Delete All" for Agency Users */}
                    {user.role === 'agency' && (
                        <button
                            onClick={() => {
                                setShowDeleteAllConfirm(true);
                            }}
                            className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
                        >
                            <Trash2 size={18} />
                            {t('actions.delete_all_assets')}
                        </button>
                    )}
                </div>
                <div className="flex gap-2">
                    {selectedIds.size > 0 && !isAssetManager && (
                        <button
                            onClick={() => setShowBulkConfirm(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition shadow-sm animate-in fade-in zoom-in duration-200"
                        >
                            <Trash2 size={18} />
                            <span className="font-medium">Delete ({selectedIds.size})</span>
                        </button>
                    )}
                    {selectedIds.size > 0 && (
                        <button
                            onClick={() => {
                                setPrintAssets(assets.filter(a => selectedIds.has(a.id)));
                                setShowBulkLabelModal(true);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-[#0088CC] text-white rounded-lg hover:bg-[#0077B3] transition shadow-sm animate-in fade-in zoom-in duration-200"
                        >
                            <Printer size={18} />
                            <span className="font-medium">{t('actions.print_labels', 'Labels')} ({selectedIds.size})</span>
                        </button>
                    )}
                    {!isAssetManager && (
                        <button
                            onClick={() => setShowImport(true)}
                            className="p-2.5 text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                            title="Import Assets"
                        >
                            <Upload size={20} />
                            <span className="sr-only">Import Assets</span>
                        </button>
                    )}
                    <button
                        onClick={handleExportCSV}
                        className="p-2.5 text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                        title="Export CSV"
                    >
                        <Download size={20} />
                        <span className="sr-only">Export CSV</span>
                    </button>
                    {!isAssetManager && (
                        <button
                            onClick={handlePrintAllLabels}
                            className="p-2.5 text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                            title={t('actions.print_all_labels', 'Print All Labels')}
                        >
                            <Printer size={20} />
                            <span className="sr-only">Print All Labels</span>
                        </button>
                    )}
                    <button
                        onClick={handleExportPDF}
                        className="p-2.5 text-red-600 bg-white border border-slate-200 rounded-lg hover:bg-red-50 transition-colors"
                        title="Export PDF"
                    >
                        <FileText size={20} />
                        <span className="sr-only">Export PDF</span>
                    </button>
                    {!isAssetManager && (
                        <button
                            onClick={() => { setEditAsset(null); setViewMode('wizard'); }}
                            className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                        >
                            <Plus size={20} />
                            <span className="hidden sm:inline">{t('add_asset')}</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Import Modal */}
            <AssetImportModal
                isOpen={showImport}
                onClose={() => setShowImport(false)}
                user={user}
                existingCategories={categories}
                existingSuppliers={suppliers}
                existingCostCenters={costCenters}
                existingBranches={branches}
                existingTeam={team}
                onSuccess={() => {
                    loadInitialData();
                    loadAssets();
                }}
            />

            {/* Bulk Label Modal */}
            {showBulkLabelModal && (
                <AssetBulkLabelModal
                    assets={printAssets}
                    companyName={agencyName}
                    onClose={() => setShowBulkLabelModal(false)}
                />
            )}


            {/* Filters */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                <div className="relative">
                    <Search className="absolute left-3 top-3 text-slate-400" size={20} />
                    <input
                        type="text"
                        placeholder="Αναζήτηση με κωδικό, περιγραφή, serial number, barcode..."
                        className="w-full border border-slate-200 pl-10 p-3 rounded-xl bg-slate-50 focus:bg-white transition outline-none focus:ring-2 focus:ring-blue-100"
                        value={filters.search}
                        onChange={e => handleFilterChange('search', e.target.value)}
                    />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <select value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} className="border border-slate-200 p-2.5 rounded-lg bg-slate-50 text-sm font-medium text-slate-700">
                        <option value="">{t('status.all_statuses', 'Όλες οι Καταστάσεις')}</option>
                        {[
                            'active', 'temporarily_inactive', 'inactive', 'fully_depreciated',
                            'for_sale', 'sold', 'deleted', 'written_off', 'cancelled',
                            'to_be_destroyed', 'to_be_moved'
                        ].map(status => (
                            <option key={status} value={status}>{t(`status.${status}`)}</option>
                        ))}
                    </select>
                    <select value={filters.branchId} onChange={e => handleFilterChange('branchId', e.target.value)} className="border border-slate-200 p-2.5 rounded-lg bg-slate-50 text-sm font-medium text-slate-700">
                        <option value="">Υποκαταστήματα</option>
                        {branches.map(b => <option key={b.id} value={b.id}>{b.profile?.companyName || b.config?.companyName}</option>)}
                    </select>
                    <select value={filters.costCenterId} onChange={e => handleFilterChange('costCenterId', e.target.value)} className="border border-slate-200 p-2.5 rounded-lg bg-slate-50 text-sm font-medium text-slate-700">
                        <option value="">Τμήματα</option>
                        {costCenters.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                    <select
                        value={filterParentId}
                        onChange={e => {
                            const pid = e.target.value;
                            setFilterParentId(pid);
                            // If clearing parent or changing it, reset category filter to the parent (or empty)
                            handleFilterChange('categoryId', pid);
                        }}
                        className="border border-slate-200 p-2.5 rounded-lg bg-slate-50 text-sm font-medium text-slate-700"
                    >
                        <option value="">{t('wizard.step_1.placeholders.select_category')}</option>
                        {categories
                            .filter(c => !c.parentId)
                            .sort((a, b) => a.title.localeCompare(b.title))
                            .map(c => <option key={c.id} value={c.id}>{c.title}</option>)
                        }
                    </select>

                    {filterParentId && (
                        <select
                            value={categories.find(c => c.id === filters.categoryId)?.parentId === filterParentId ? filters.categoryId : ''}
                            onChange={e => {
                                const subId = e.target.value;
                                // If sub selected, use it. If cleared, fallback to parent.
                                handleFilterChange('categoryId', subId || filterParentId);
                            }}
                            className="border border-slate-200 p-2.5 rounded-lg bg-slate-50 text-sm font-medium text-slate-700"
                        >
                            <option value="">{t('wizard.step_1.placeholders.select_subcategory')}</option>
                            {categories
                                .filter(c => c.parentId === filterParentId)
                                .sort((a, b) => a.title.localeCompare(b.title))
                                .map(c => <option key={c.id} value={c.id}>{c.title}</option>)
                            }
                        </select>
                    )}
                    <select value={filters.supplierId} onChange={e => handleFilterChange('supplierId', e.target.value)} className="border border-slate-200 p-2.5 rounded-lg bg-slate-50 text-sm font-medium text-slate-700">
                        <option value="">Προμηθευτές</option>
                        {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <select
                        value={filters.assetManagerId}
                        onChange={e => handleFilterChange('assetManagerId', e.target.value)}
                        className={`border border-slate-200 p-2.5 rounded-lg bg-slate-50 text-sm font-medium text-slate-700 ${isAssetManager ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={isAssetManager}
                    >
                        <option value="">Υπεύθυνοι</option>
                        {team
                            .filter((m: any) => m.memberProfile?.teamRole === 'asset_manager')
                            .map((m: any) => (
                                <option key={m.id} value={m.id}>{m.memberProfile?.name || m.displayName || m.email}</option>
                            ))
                        }
                    </select>
                    <select
                        value={filters.assetUserId}
                        onChange={e => handleFilterChange('assetUserId', e.target.value)}
                        className="border border-slate-200 p-2.5 rounded-lg bg-slate-50 text-sm font-medium text-slate-700"
                    >
                        <option value="">Χρήστες</option>
                        {team
                            .filter((m: any) => m.memberProfile?.teamRole === 'asset_user' || m.memberProfile?.teamRole === 'asset_manager')
                            .map((m: any) => (
                                <option key={m.id} value={m.id}>{m.memberProfile?.name || m.displayName || m.email}</option>
                            ))
                        }
                    </select>

                    <button
                        onClick={() => {
                            setFilters({
                                search: '',
                                status: '',
                                branchId: '',
                                costCenterId: '',
                                categoryId: '',
                                supplierId: '',
                                assetManagerId: isAssetManager ? user.id : '',
                                assetUserId: ''
                            });
                            setFilterParentId('');
                            setCurrentPage(1);
                            setCursors([null]);
                            setLastVisible(null);
                        }}
                        className="p-2.5 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-red-500 transition flex items-center gap-2 text-sm font-bold"
                        title={t('import.mapping.clear_filters')}
                    >
                        <RotateCcw size={16} />
                        <span className="hidden md:inline">{t('import.mapping.clear_filters')}</span>
                    </button>
                </div>
            </div>

            {/* Error Message for Indexing */}
            {
                error && (
                    <div className="p-4 bg-yellow-50 text-yellow-800 rounded-2xl border border-yellow-200 flex items-center gap-3">
                        <Loader2 className="animate-spin text-yellow-600" size={20} />
                        <div>
                            <p className="font-bold">System Optimization in Progress</p>
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                )
            }

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-500 font-medium">
                        <tr>
                            <th className="pl-6 py-4 w-10 border-l-4 border-transparent">
                                <input
                                    type="checkbox"
                                    checked={assets.length > 0 && selectedIds.size === assets.length}
                                    onChange={toggleSelectAll}
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                            </th>
                            <th className="px-6 py-4">{t('table.code')}</th>
                            <th className="px-6 py-4">{t('table.barcode')}</th>
                            <th className="px-6 py-4">{t('table.description')}</th>
                            <th className="px-6 py-4">{t('table.category')}</th>
                            <th className="px-6 py-4">{t('table.branch')}</th>
                            <th className="px-6 py-4">{t('table.status')}</th>
                            <th className="px-6 py-4 text-right">{t('table.value')}</th>
                            {!isAssetManager && <th className="px-6 py-4 text-right">{t('table.actions')}</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading || metaLoading ? (
                            <tr><td colSpan={8} className="p-8 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" /></td></tr>
                        ) : assets.length === 0 ? (
                            <tr><td colSpan={8} className="p-8 text-center text-slate-400">No assets found.</td></tr>
                        ) : (
                            // Render Strategy: Iterate original assets. If it's a child, skip (handled by master). If master, render it + children if expanded.
                            // Problem: Orphans (children without master on page).
                            assets.filter(a => !a.masterSerial || !assets.find(p => p.id === (a.groupId || a.masterSerial))).map(asset => {
                                const isMaster = !asset.masterSerial;
                                const gid = asset.groupId || asset.id;
                                const group = groups[gid]; // From memo
                                const children = group?.children || [];
                                const hasChildren = children.length > 0;
                                const isExpanded = expandedGroups.has(gid);

                                // If this asset IS a child but handled here, it means it's an orphan on this page.
                                // We render it as normal row.
                                if (!isMaster) {
                                    // ORPHAN RENDER (Normal)
                                    return (
                                        <AssetRow
                                            userCurrency={user.currency || 'EUR'}
                                            key={asset.id}
                                            asset={asset}
                                            t={t}
                                            getCategoryInfo={getCategoryInfo}
                                            getBranchName={getBranchName}
                                            onEdit={() => { setEditAsset(asset); setViewMode('wizard'); }}
                                            onDelete={!isAssetManager ? () => handleDeleteAsset(asset.id) : undefined}
                                            setSelectedAsset={setSelectedAsset}
                                            isSelected={selectedIds.has(asset.id)}
                                            onToggleSelect={() => toggleSelect(asset.id)}
                                            isAssetManager={isAssetManager}
                                        />
                                    );
                                }

                                // MASTER RENDER
                                return (
                                    <React.Fragment key={asset.id}>
                                        <AssetRow
                                            userCurrency={user.currency || 'EUR'}
                                            asset={asset}
                                            t={t}
                                            getCategoryInfo={getCategoryInfo}
                                            getBranchName={getBranchName}
                                            onEdit={() => { setEditAsset(asset); setViewMode('wizard'); }}
                                            onDelete={!isAssetManager ? () => handleDeleteAsset(asset.id) : undefined}
                                            setSelectedAsset={setSelectedAsset}
                                            isSelected={selectedIds.has(asset.id)}
                                            onToggleSelect={() => toggleSelect(asset.id)}
                                            hasChildren={hasChildren}
                                            isExpanded={isExpanded}
                                            onToggleExpand={() => toggleGroup(gid)}
                                            isAssetManager={isAssetManager}
                                        />
                                        {isExpanded && children.map(child => (
                                            <AssetRow
                                                userCurrency={user.currency || 'EUR'}
                                                key={child.id}
                                                asset={child}
                                                t={t}
                                                getCategoryInfo={getCategoryInfo}
                                                getBranchName={getBranchName}
                                                onEdit={() => { setEditAsset(child); setViewMode('wizard'); }}
                                                onDelete={!isAssetManager ? () => handleDeleteAsset(child.id) : undefined}
                                                setSelectedAsset={setSelectedAsset}
                                                isSelected={selectedIds.has(child.id)}
                                                onToggleSelect={() => toggleSelect(child.id)}
                                                isChild
                                                isAssetManager={isAssetManager}
                                            />
                                        ))}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
                {/* Pagination Controls */}
                <div className="flex justify-between items-center p-4 border-t border-slate-100 bg-slate-50">
                    <span className="text-slate-500 text-sm font-medium">
                        Page {currentPage} <span className="mx-2 text-slate-300">|</span> Total: {totalCount}
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={onPrev}
                            disabled={currentPage === 1}
                            className="bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                            <ChevronLeft size={16} /> Prev
                        </button>
                        <button
                            onClick={onNext}
                            disabled={!hasMore}
                            className="bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                            Next <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>


            {/* Detail Panel */}
            {
                selectedAsset && (
                    <AssetDetailPanel
                        asset={selectedAsset}
                        currentUser={user}
                        isAssetManager={isAssetManager}
                        onClose={() => setSelectedAsset(null)}
                        onEdit={(a) => {
                            setSelectedAsset(null);
                            setEditAsset(a);
                            setViewMode('wizard');
                        }}
                        categories={categories}
                        branches={branches}
                        costCenters={costCenters}
                        suppliers={suppliers}
                        team={team}
                        dateFormat={user.dateFormat}
                        onAssetUpdated={() => {
                            loadAssets();
                            // Also update selectedAsset to match the new local changes immediately if strictly needed,
                            // but loadAssets will eventually refresh the list. 
                            // Since AssetDetailPanel uses local 'displayAsset', the panel itself is fine.
                            // We just need the background list to update.
                        }}
                    />
                )
            }

            {/* Backdrop for panel */}
            {
                selectedAsset && (
                    <div
                        className="fixed inset-0 bg-black/20 z-40 backdrop-blur-sm animate-in fade-in duration-300"
                        onClick={() => setSelectedAsset(null)}
                    />
                )
            }
            {/* Error Modal */}
            {
                deleteError && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
                            <div className="flex items-center gap-3 text-red-600 mb-4">
                                <AlertTriangle size={32} />
                                <h3 className="text-lg font-bold">{t('delete_error_title')}</h3>
                            </div>
                            <p className="text-slate-600 mb-6 leading-relaxed">
                                {deleteError}
                            </p>
                            <div className="flex justify-end">
                                <button
                                    onClick={() => setDeleteError(null)}
                                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg font-medium transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* Bulk Confirmation Modal */}
            {
                showBulkConfirm && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
                            <div className="flex items-center gap-3 text-red-600 mb-4">
                                <AlertTriangle size={32} />
                                <h3 className="text-lg font-bold">{t('delete_modal_title')}</h3>
                            </div>
                            <p className="text-slate-600 mb-6 leading-relaxed">
                                {t('confirm_bulk_delete', { count: selectedIds.size })}
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowBulkConfirm(false)}
                                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg font-medium transition-colors"
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmBulkDeletion}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                                    disabled={isDeleting}
                                >
                                    {isDeleting && <Loader2 size={16} className="animate-spin" />}
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Confirmation Modal */}
            {
                assetToDelete && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
                            <div className="flex items-center gap-3 text-red-600 mb-4">
                                <AlertTriangle size={32} />
                                <h3 className="text-lg font-bold">{t('delete_modal_title')}</h3>
                            </div>
                            <p className="text-slate-600 mb-6 leading-relaxed">
                                {t('confirm_delete')}
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setAssetToDelete(null)}
                                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg font-medium transition-colors"
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeletion}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                                    disabled={isDeleting}
                                >
                                    {isDeleting && <Loader2 size={16} className="animate-spin" />}
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Delete All Modal */}
            {showDeleteAllConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center gap-4 mb-6 text-red-600">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                                <Trash2 size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-slate-900">{t('modals.delete_all_title')}</h3>
                                <p className="text-sm text-slate-500 font-medium">This action is irreversible</p>
                            </div>
                        </div>

                        <p className="text-gray-600 mb-6">
                            {t('modals.delete_all_warning')}
                            <br /><br />
                            <span className="font-semibold text-red-600">
                                This action cannot be undone. All assets and their history will be permanently removed.
                            </span>
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowDeleteAllConfirm(false)}
                                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg font-medium transition-colors"
                                disabled={isDeletingAll}
                            >
                                {t('actions.cancel')}
                            </button>
                            <button
                                onClick={handleDeleteAll}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                                disabled={isDeletingAll}
                            >
                                {isDeletingAll && <Loader2 size={16} className="animate-spin" />}
                                {t('actions.confirm_delete_all')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

// --- HELPER COMPONENTS ---


interface AssetRowProps {
    asset: Asset;
    t: any;
    getCategoryInfo: (id: string) => { title: string, subTitle?: string };
    getBranchName: (id: string) => string;
    onEdit: () => void;
    onDelete?: () => void;
    setSelectedAsset: (a: Asset) => void;
    hasChildren?: boolean;
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    isChild?: boolean;
    isSelected?: boolean;
    onToggleSelect?: () => void;
    isAssetManager?: boolean;
    userCurrency?: string;
}

const AssetRow: React.FC<AssetRowProps> = ({
    asset, t, getCategoryInfo, getBranchName, onEdit, onDelete, setSelectedAsset,
    hasChildren, isExpanded, onToggleExpand, isChild, isSelected, onToggleSelect, isAssetManager, userCurrency
}) => {
    return (
        <tr
            className={`group hover:bg-slate-50 cursor-pointer transition-colors ${isChild ? 'bg-slate-50/50' : ''} ${isSelected ? 'bg-blue-50' : ''}`}
            onClick={() => setSelectedAsset(asset)}
        >
            <td className="pl-6 py-4 w-10 border-l-4 border-transparent group-hover:border-blue-500 transition-all align-middle" onClick={(e) => e.stopPropagation()}>
                <input
                    type="checkbox"
                    checked={isSelected || false}
                    onChange={() => onToggleSelect && onToggleSelect()}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
            </td>
            <td className="px-6 py-4 font-mono text-slate-600 align-middle">
                <div className="flex items-center gap-2">
                    {isChild && <div className="w-4 h-4 border-l border-b border-slate-300 ml-2 mr-1"></div>}

                    {hasChildren && !isChild && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onToggleExpand) onToggleExpand();
                            }}
                            className="p-1 hover:bg-slate-200 text-slate-500 rounded mr-1 transition-colors"
                        >
                            <ChevronRight
                                className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                                size={18}
                            />
                        </button>
                    )}

                    {asset.code}
                </div>
            </td>
            <td className="px-6 py-4 font-mono text-slate-500 text-sm align-middle">
                {asset.barcode || '-'}
            </td>
            <td className="px-6 py-4 align-middle">
                <div className="font-bold text-slate-900">{asset.description}</div>
                {asset.supplierDescription && <div className="text-xs text-slate-500 mt-0.5">{asset.supplierDescription}</div>}
            </td>
            <td className="px-6 py-4 align-middle">
                {(() => {
                    const info = getCategoryInfo(asset.categoryId);
                    return (
                        <div className="flex flex-col">
                            <span className="font-medium text-slate-800">{info.title}</span>
                            {info.subTitle && <span className="text-xs text-slate-500">{info.subTitle}</span>}
                        </div>
                    );
                })()}
            </td>
            <td className="px-6 py-4 align-middle">
                {getBranchName(asset.branchId)}
            </td>
            <td className="px-6 py-4 align-middle">
                <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${asset.status === 'active' ? 'bg-green-100 text-green-700' :
                    asset.status === 'deleted' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                    {t(`status.${asset.status}`)}
                </span>
            </td>
            <td className="px-6 py-4 text-right font-mono font-bold align-middle">{formatCurrency(asset.totalValue || 0, userCurrency || 'EUR')}</td>
            {!isAssetManager && (
                <td className="px-6 py-4 text-right align-middle">
                    <div className="flex justify-end gap-2">
                        <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition" title={t('actions.edit')}>
                            <FileText size={18} />
                        </button>
                        {onDelete && (
                            <button onClick={async (e) => {
                                e.stopPropagation();
                                onDelete();
                            }} className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition" title={t('actions.delete')}>
                                <X size={18} />
                            </button>
                        )}
                    </div>
                </td>
            )}
        </tr>
    );
};

// --- WIZARD COMPONENT ---

interface AssetWizardProps {
    user: User;
    onClose: () => void;
    onSuccess: () => void;
    initialData?: Asset | null;
}

const AssetWizard: React.FC<AssetWizardProps> = ({ user, onClose, onSuccess, initialData }) => {
    const { t } = useTranslation('assets');
    const isAssetManager = user.memberProfile?.teamRole === 'asset_manager';
    const [step, setStep] = useState(0); // 0=Identity, 1=Financials, 2=Location, 3=Review
    const [formData, setFormData] = useState<any>(initialData || INITIAL_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // Data Sources
    const [categories, setCategories] = useState<AssetCategory[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [branches, setBranches] = useState<User[]>([]); // Clients are Users (Branches)
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [team, setTeam] = useState<User[]>([]);

    useEffect(() => {
        if (initialData) {
            setFormData(initialData);
        }
    }, [initialData]);

    useEffect(() => {
        const agencyId = user.agencyId || user.id;
        const fetchData = async () => {
            try {
                const [cats, supps, ccenters, allAgencyUsers] = await Promise.all([
                    api.getAssetCategories(agencyId),
                    api.getSuppliers(agencyId),
                    api.getCostCenters(agencyId),
                    api.getAgencyClients(agencyId, undefined, true)
                ]);
                setCategories(cats);
                setSuppliers(supps);
                setCostCenters(ccenters);

                // Split Users into Branches (Clients) and Team (Members/Agency)
                let _branches = allAgencyUsers.filter(u => u.role !== 'agency_member' && u.role !== 'agency');
                const _team = allAgencyUsers.filter(u => u.role === 'agency_member' || u.role === 'agency');

                // Asset Manager Constraint: Only see assigned branches
                if (isAssetManager) {
                    _branches = _branches.filter(b => b.assignedAgentIds?.includes(user.id));
                }

                // Ensure current user is in team if they are agency owner (just in case)
                if (user.role === 'agency' && !_team.find(u => u.id === user.id)) {
                    _team.push(user);
                }

                setBranches(_branches);
                setTeam(_team);
            } catch (e) {
                console.error("Failed to load wizard data", e);
            }
        };
        fetchData();
    }, []);

    // Calculated Fields Watcher
    useEffect(() => {
        const base = (formData.quantity || 0) * (formData.unitValue || 0);
        const discount = base * ((formData.discountStats || 0) / 100);
        const total = base - discount;
        setFormData((prev: any) => ({ ...prev, totalValue: total }));
    }, [formData.quantity, formData.unitValue, formData.discountStats]);

    const handleNext = () => { if (step < 3) setStep(step + 1); };
    const handleBack = () => { if (step > 0) setStep(step - 1); };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const agencyId = user.agencyId || user.id;
            if (initialData && initialData.id) {
                await api.updateAsset(initialData.id, formData);
            } else {
                await api.createAsset(agencyId, formData);
            }
            // alert(t('wizard.success'));
            // onSuccess();
            setShowSuccessModal(true);
        } catch (e) {
            console.error(e);
            alert('Error creating/updating asset');
        } finally {
            setSubmitting(false);
        }
    };

    const StepsIndicator = () => (
        <div className="flex items-center justify-center mb-8 gap-4">
            {STEPS.map((s, idx) => (
                <div key={s} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step === idx ? 'bg-blue-600 text-white' : step > idx ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                        {step > idx ? <Check size={16} /> : idx + 1}
                    </div>
                    {idx < STEPS.length - 1 && (
                        <div className={`w-8 h-1 mx-2 ${step > idx ? 'bg-green-500' : 'bg-slate-200'}`} />
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex justify-between items-center z-10">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">{t('wizard.title')}</h2>
                    <div className="flex items-center gap-2 text-xs">
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-bold uppercase">{t('wizard.mode')}</span>
                        <span className="text-slate-400">Step {step + 1} of 4</span>
                    </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full transition"><X size={24} className="text-slate-400" /></button>
            </div>

            <div className="max-w-4xl mx-auto p-8 pb-32">
                <StepsIndicator />

                {/* Step Content */}
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    {step === 0 && <StepIdentity t={t} formData={formData} setFormData={setFormData} categories={categories} />}
                    {step === 1 && <StepFinancials t={t} formData={formData} setFormData={setFormData} suppliers={suppliers} user={user} />}
                    {step === 2 && <StepLocation t={t} formData={formData} setFormData={setFormData} costCenters={costCenters} user={user} branches={branches} team={team} />}
                    {step === 3 && <StepReview t={t} formData={formData} setStep={setStep} categories={categories} suppliers={suppliers} branches={branches} costCenters={costCenters} team={team} userCurrency={user.currency || 'EUR'} />}
                </div>
            </div>

            {/* Footer */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 p-4 flex justify-between items-center z-10 max-w-full">
                <button
                    onClick={handleBack}
                    disabled={step === 0}
                    className="flex items-center gap-2 px-6 py-3 text-slate-500 font-bold hover:text-slate-800 disabled:opacity-30 transition"
                >
                    <ChevronLeft size={20} /> {t('wizard.back')}
                </button>

                {step === 3 ? (
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="bg-green-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-green-700 transition shadow-lg shadow-green-200"
                    >
                        {submitting && <Loader2 className="animate-spin inline-block mr-2" size={20} />}
                        {submitting ? t('wizard.submitting_btn') : t('wizard.submit_btn')}
                    </button>
                ) : (
                    <button
                        onClick={handleNext}
                        className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-700 transition flex items-center gap-2 shadow-lg shadow-blue-200"
                    >
                        {t('wizard.next')} <ChevronRight size={20} />
                    </button>
                )}
            </div>

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 animate-in zoom-in-95 duration-200 text-center">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto text-green-600 mb-6">
                            <Check size={40} strokeWidth={4} />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">{t('wizard.success')}</h3>
                        <p className="text-slate-500 mb-8">
                            {initialData ? 'Το πάγιο ενημερώθηκε επιτυχώς.' : 'Το νέο πάγιο δημιουργήθηκε και καταχωρήθηκε στο μητρώο.'}
                        </p>
                        <button
                            onClick={onSuccess}
                            className="w-full bg-slate-900 text-white py-3.5 rounded-xl font-bold hover:bg-slate-800 transition shadow-lg hover:shadow-xl"
                        >
                            {t('actions.close')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- SUB COMPONENTS ---

const StepIdentity = ({ t, formData, setFormData, categories }: any) => {
    // Helper to determine parent/child state
    const currentCategory = categories.find((c: any) => c.id === formData.categoryId);
    // If current category is a child, its parent is parentId. If it's a root (no parentId), it's the parent itself.
    const selectedParentId = currentCategory?.parentId || (currentCategory ? currentCategory.id : '') || '';

    // Derived lists
    const parentCategories = categories.filter((c: any) => !c.parentId).sort((a: any, b: any) => a.title.localeCompare(b.title));
    const subCategories = selectedParentId ? categories.filter((c: any) => c.parentId === selectedParentId).sort((a: any, b: any) => a.title.localeCompare(b.title)) : [];

    return (
        <div className="space-y-8">
            <div>
                <h3 className="text-xl font-bold text-slate-900">{t('wizard.step_1.title')}</h3>
                <p className="text-slate-500">{t('wizard.step_1.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label={t('wizard.step_1.description')} required>
                    <input type="text" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className="w-full border p-3 rounded-xl" placeholder={t('wizard.step_1.placeholders.description')} />
                </Field>
                <Field label={t('wizard.step_1.supplier_desc')}>
                    <input type="text" value={formData.supplierDescription} onChange={e => setFormData({ ...formData, supplierDescription: e.target.value })} className="w-full border p-3 rounded-xl" placeholder={t('wizard.step_1.placeholders.supplier_desc')} />
                </Field>
                <Field label={t('wizard.step_1.code')} required>
                    <div className="relative">
                        <Tag className="absolute left-3 top-3 text-slate-400" size={18} />
                        <input type="text" value={formData.code} onChange={e => setFormData({ ...formData, code: e.target.value })} className="w-full border p-3 pl-10 rounded-xl font-mono" placeholder={t('wizard.step_1.placeholders.code')} />
                    </div>
                </Field>
                <Field label={t('wizard.step_1.category')} required>
                    <div className="flex gap-2">
                        <select
                            value={selectedParentId}
                            onChange={e => {
                                const newParentId = e.target.value;
                                // If switching parent, verify if there are children? 
                                // Default to selecting the parent itself.
                                setFormData({ ...formData, categoryId: newParentId });
                            }}
                            className="w-full border p-3 rounded-xl bg-white flex-1"
                        >
                            <option value="">{t('wizard.step_1.placeholders.select_category')}</option>
                            {parentCategories.map((c: any) => (
                                <option key={c.id} value={c.id}>{c.title} - {c.code}</option>
                            ))}
                        </select>

                        {/* Subcategory Dropdown - Show if parent selected and has children OR if we are just showing the slot */}
                        {/* Only show if we have a parent selected. */}
                        {selectedParentId && subCategories.length > 0 && (
                            <select
                                value={currentCategory?.parentId ? currentCategory.id : ''}
                                onChange={e => {
                                    const newChildId = e.target.value;
                                    // If value is empty, it means we reverted to "Parent Only" (if allowed?) logic:
                                    // If they select the placeholder, we go back to parentId
                                    setFormData({ ...formData, categoryId: newChildId || selectedParentId });
                                }}
                                className="w-full border p-3 rounded-xl bg-white flex-1"
                            >
                                <option value="">{t('wizard.step_1.placeholders.select_subcategory')}</option>
                                {subCategories.map((c: any) => (
                                    <option key={c.id} value={c.id}>{c.title} - {c.code}</option>
                                ))}
                            </select>
                        )}
                    </div>
                </Field>
                <Field label={t('wizard.step_1.manufacturer')}>
                    <div className="relative">
                        <Briefcase className="absolute left-3 top-3 text-slate-400" size={18} />
                        <input type="text" value={formData.manufacturer} onChange={e => setFormData({ ...formData, manufacturer: e.target.value })} className="w-full border p-3 pl-10 rounded-xl" placeholder={t('wizard.step_1.placeholders.select_manufacturer')} />
                    </div>
                </Field>
                <Field label={t('wizard.step_1.model')}>
                    <div className="relative">
                        <Smartphone className="absolute left-3 top-3 text-slate-400" size={18} />
                        <input type="text" value={formData.model} onChange={e => setFormData({ ...formData, model: e.target.value })} className="w-full border p-3 pl-10 rounded-xl" placeholder={t('wizard.step_1.placeholders.model')} />
                    </div>
                </Field>
                <Field label={t('wizard.step_1.serial')}>
                    <input type="text" value={formData.serialNumber} onChange={e => setFormData({ ...formData, serialNumber: e.target.value })} className="w-full border p-3 rounded-xl" placeholder={t('wizard.step_1.placeholders.serial')} />
                </Field>
                <Field label={t('wizard.step_1.master_serial')}>
                    <div className="relative">
                        <span className="absolute left-3 top-3 text-slate-400 font-mono text-xs border border-slate-200 px-1 rounded">M</span>
                        <input
                            type="text"
                            value={formData.masterSerial || ''}
                            onChange={e => setFormData({ ...formData, masterSerial: e.target.value })}
                            className="w-full border p-3 pl-10 rounded-xl"
                            placeholder={t('wizard.step_1.placeholders.master_serial')}
                        />
                    </div>
                    <p className="text-xs text-slate-400 mt-1 ml-1">{t('wizard.step_1.master_serial_hint', 'Optional: Link to a Master Asset')}</p>
                </Field>
                <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-6">
                    <Field label={t('wizard.step_1.color', 'Color')}>
                        <input type="text" value={formData.color || ''} onChange={e => setFormData({ ...formData, color: e.target.value })} className="w-full border p-3 rounded-xl" placeholder={t('wizard.step_1.placeholders.color', 'e.g. Red')} />
                    </Field>
                    <Field label={t('wizard.step_1.height', 'Height')}>
                        <input type="text" value={formData.height || ''} onChange={e => setFormData({ ...formData, height: e.target.value })} className="w-full border p-3 rounded-xl" placeholder={t('wizard.step_1.placeholders.height', 'e.g. 10 cm')} />
                    </Field>
                    <Field label={t('wizard.step_1.length', 'Length')}>
                        <input type="text" value={formData.length || ''} onChange={e => setFormData({ ...formData, length: e.target.value })} className="w-full border p-3 rounded-xl" placeholder={t('wizard.step_1.placeholders.length', 'e.g. 20 cm')} />
                    </Field>
                    <Field label={t('wizard.step_1.width', 'Width')}>
                        <input type="text" value={formData.width || ''} onChange={e => setFormData({ ...formData, width: e.target.value })} className="w-full border p-3 rounded-xl" placeholder={t('wizard.step_1.placeholders.width', 'e.g. 5 cm')} />
                    </Field>
                </div>
                <Field label={t('wizard.step_1.barcode')}>
                    <div className="relative">
                        <Barcode className="absolute left-3 top-3 text-slate-400" size={18} />
                        <input type="text" value={formData.barcode} onChange={e => setFormData({ ...formData, barcode: e.target.value })} className="w-full border p-3 pl-10 rounded-xl font-mono" placeholder={t('wizard.step_1.placeholders.barcode')} />
                    </div>
                </Field>
            </div>
        </div>
    );
};

const StepFinancials = ({ t, formData, setFormData, suppliers, user }: any) => (
    <div className="space-y-8">
        <div>
            <h3 className="text-xl font-bold text-slate-900">{t('wizard.step_2.title')}</h3>
            <p className="text-slate-500">{t('wizard.step_2.subtitle')}</p>
        </div>

        {/* Value Calculation Card */}
        <div className="bg-sky-50 p-6 rounded-2xl border border-sky-100 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Field label={t('wizard.step_2.quantity')}>
                <input type="number" min="1" value={formData.quantity} onChange={e => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })} className="w-full border border-sky-200 p-3 rounded-xl text-center text-lg font-bold" />
            </Field>
            <Field label={t('wizard.step_2.unit_value')}>
                <div className="relative">
                    <input type="number" min="0" step="0.01" value={formData.unitValue} onChange={e => setFormData({ ...formData, unitValue: parseFloat(e.target.value) || 0 })} className="w-full border border-sky-200 p-3 pr-8 rounded-xl text-right text-lg font-bold" />
                    <Euro size={16} className="absolute right-3 top-4 text-slate-400" />
                </div>
            </Field>
            <Field label={t('wizard.step_2.discount')}>
                <div className="relative">
                    <input type="number" min="0" max="100" value={formData.discountStats} onChange={e => setFormData({ ...formData, discountStats: parseFloat(e.target.value) || 0 })} className="w-full border border-sky-200 p-3 pr-8 rounded-xl text-right text-lg font-bold" />
                    <span className="absolute right-3 top-4 text-slate-400 font-bold">%</span>
                </div>
            </Field>
            <div className="md:col-span-3 flex justify-end items-center gap-4 pt-4 border-t border-sky-200">
                <span className="text-sky-700 font-bold uppercase text-sm tracking-wider">{t('wizard.step_2.total_value')}</span>
                <span className="text-3xl font-black text-sky-900">{formatCurrency(formData.totalValue || 0, user.currency || 'EUR')}</span>
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label={t('wizard.step_2.acquisition_date')}>
                <LocalizedDatePicker
                    value={formData.acquisitionDate}
                    onChange={val => setFormData({ ...formData, acquisitionDate: val })}
                    format={user.dateFormat || 'DD/MM/YYYY'}
                    className="w-full border p-3 rounded-xl"
                />
            </Field>
            <Field label={t('wizard.step_2.residual_value')}>
                <input type="number" value={formData.residualValue} onChange={e => setFormData({ ...formData, residualValue: parseFloat(e.target.value) })} className="w-full border p-3 rounded-xl" />
            </Field>
            <Field label={t('wizard.step_2.vat')}>
                <input type="number" value={formData.vatPercent} onChange={e => setFormData({ ...formData, vatPercent: parseFloat(e.target.value) })} className="w-full border p-3 rounded-xl" />
            </Field>
            <Field label={t('wizard.step_2.supplier')}>
                <select value={formData.supplierId} onChange={e => setFormData({ ...formData, supplierId: e.target.value })} className="w-full border p-3 rounded-xl bg-white">
                    <option value="">{t('wizard.step_2.placeholders.select_supplier')}</option>
                    {suppliers.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
            </Field>
            <Field label={t('wizard.step_2.invoice_number')}>
                <input type="text" value={formData.invoiceNumber} onChange={e => setFormData({ ...formData, invoiceNumber: e.target.value })} className="w-full border p-3 rounded-xl" placeholder={t('wizard.step_2.placeholders.invoice_number')} />
            </Field>
            <Field label={t('wizard.step_2.invoice_date')}>
                <LocalizedDatePicker
                    value={formData.invoiceDate}
                    onChange={val => setFormData({ ...formData, invoiceDate: val })}
                    format={user.dateFormat || 'DD/MM/YYYY'}
                    className="w-full border p-3 rounded-xl"
                />
            </Field>
            <Field label={t('wizard.step_2.sent_accounting')}>
                <LocalizedDatePicker
                    value={formData.sentToAccountingDate}
                    onChange={val => setFormData({ ...formData, sentToAccountingDate: val })}
                    format={user.dateFormat || 'DD/MM/YYYY'}
                    className="w-full border p-3 rounded-xl"
                />
            </Field>
            <Field label={t('wizard.step_2.usage_start_date')}>
                <LocalizedDatePicker
                    value={formData.usageStartDate}
                    onChange={val => setFormData({ ...formData, usageStartDate: val })}
                    format={user.dateFormat || 'DD/MM/YYYY'}
                    className="w-full border p-3 rounded-xl"
                />
            </Field>
        </div>
    </div>
);

const StepLocation = ({ t, formData, setFormData, costCenters, user, branches, team }: any) => (
    <div className="space-y-8">
        <div>
            <h3 className="text-xl font-bold text-slate-900">{t('wizard.step_4.title')}</h3>
            <p className="text-slate-500">{t('wizard.step_4.subtitle')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label={t('wizard.step_4.status')} required>
                <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} className="w-full border p-3 rounded-xl bg-white font-bold text-slate-700">
                    <option value="active">{t('status.active')}</option>
                    <option value="temporarily_inactive">{t('status.temporarily_inactive')}</option>
                    <option value="inactive">{t('status.inactive')}</option>
                    <option value="fully_depreciated">{t('status.fully_depreciated')}</option>
                    <option value="for_sale">{t('status.for_sale')}</option>
                    <option value="sold">{t('status.sold')}</option>
                    <option value="deleted">{t('status.deleted')}</option>
                    <option value="written_off">{t('status.written_off')}</option>
                    <option value="cancelled">{t('status.cancelled')}</option>
                    <option value="to_be_destroyed">{t('status.to_be_destroyed')}</option>
                    <option value="to_be_moved">{t('status.to_be_moved')}</option>
                </select>
            </Field>

            <Field label={t('wizard.step_4.branch')}>
                <select value={formData.branchId} onChange={e => setFormData({ ...formData, branchId: e.target.value })} className="w-full border p-3 rounded-xl bg-white">
                    <option value="">{t('wizard.step_4.placeholders.select_branch')}</option>
                    {branches
                        .filter((b: any) => {
                            const name = b.displayName || b.profile?.companyName || b.config?.companyName || b.email || '';
                            return name.trim() !== 'Αθήνα (Κεντρικά)';
                        })
                        .map((b: any) => {
                            const name = b.profile?.companyName || b.config?.companyName || b.displayName || b.email;
                            const code = b.profile?.branchCode;
                            return (
                                <option key={b.id} value={b.id}>
                                    {name}{code ? ` (${code})` : ''}
                                </option>
                            );
                        })}
                </select>
            </Field>

            <Field label={t('wizard.step_4.cost_center')}>
                <select value={formData.costCenterId} onChange={e => setFormData({ ...formData, costCenterId: e.target.value })} className="w-full border p-3 rounded-xl bg-white">
                    <option value="">{t('wizard.step_4.placeholders.select_cost_center')}</option>
                    {costCenters.map((cc: any) => <option key={cc.id} value={cc.id}>{cc.title} - {cc.code}</option>)}
                </select>
            </Field>
            <Field label={t('wizard.step_4.exact_location')}>
                <div className="relative">
                    <MapPin className="absolute left-3 top-3 text-slate-400" size={18} />
                    <input type="text" value={formData.exactLocation} onChange={e => setFormData({ ...formData, exactLocation: e.target.value })} className="w-full border p-3 pl-10 rounded-xl" placeholder={t('wizard.step_4.placeholders.exact_location')} />
                </div>
            </Field>

            <div className="md:col-span-2 border-t pt-6 mt-2">
                <h4 className="font-bold text-slate-900 mb-4">Assignment</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label={t('wizard.step_4.asset_manager')}>
                        <div className="relative">
                            <Briefcase className="absolute left-3 top-3 text-slate-400" size={18} />
                            <select value={formData.assetManagerId} onChange={e => setFormData({ ...formData, assetManagerId: e.target.value })} className="w-full border p-3 pl-10 rounded-xl bg-white">
                                <option value="">{t('wizard.step_4.placeholders.select_manager')}</option>
                                {team && team
                                    .filter((m: any) => m.memberProfile?.teamRole === 'asset_manager')
                                    .map((m: any) => (
                                        <option key={m.id} value={m.id}>{m.memberProfile?.name || m.displayName || m.email}</option>
                                    ))
                                }
                            </select>
                        </div>
                    </Field>
                    <Field label={t('wizard.step_4.asset_user')}>
                        <div className="relative">
                            <UserIcon className="absolute left-3 top-3 text-slate-400" size={18} />
                            <select value={formData.assetUserId} onChange={e => setFormData({ ...formData, assetUserId: e.target.value })} className="w-full border p-3 pl-10 rounded-xl bg-white">
                                <option value="">{t('wizard.step_4.placeholders.select_user')}</option>
                                {team && team
                                    .filter((m: any) => m.memberProfile?.teamRole === 'asset_user' || m.memberProfile?.teamRole === 'asset_manager')
                                    .map((m: any) => (
                                        <option key={m.id} value={m.id}>{m.memberProfile?.name || m.displayName || m.email}</option>
                                    ))
                                }
                            </select>
                        </div>
                    </Field>
                </div>
            </div>

            {/* Notes Section */}
            <div className="border-t pt-6 mt-2">
                <Field label={t('wizard.step_4.notes', 'Notes / Comments')}>
                    <textarea
                        value={formData.notes || ''}
                        onChange={e => setFormData({ ...formData, notes: e.target.value })}
                        className="w-full border p-3 rounded-xl h-24 resize-none"
                        placeholder={t('wizard.step_4.placeholders.notes', 'Add any additional notes or comments here...')}
                    />
                </Field>
            </div>
        </div>
    </div>
);

function StepReview({ t, formData, setStep, categories, suppliers, branches, costCenters, team, userCurrency }: any) {
    const getCategoryTitle = (id: string) => {
        const c = categories?.find((c: any) => c.id === id);
        return c ? `${c.title} - ${c.code}` : id;
    };
    const getSupplierName = (id: string) => suppliers?.find((s: Supplier) => s.id === id)?.name || id;
    const getBranchName = (id: string) => branches?.find((b: any) => b.id === id)?.profile?.companyName || branches?.find((b: any) => b.id === id)?.config?.companyName || id;
    const getCostCenterName = (id: string) => {
        const c = costCenters?.find((c: any) => c.id === id);
        return c ? `${c.title} - ${c.code}` : id;
    };
    const getUserName = (id: string) => team?.find((u: any) => u.id === id)?.memberProfile?.name || team?.find((u: any) => u.id === id)?.displayName || id;

    return (
        <div className="space-y-8 text-center max-w-4xl mx-auto">
            <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto text-green-500 border-4 border-green-100 p-1">
                <div className="w-full h-full bg-green-500 rounded-full flex items-center justify-center text-white shadow-xl shadow-green-200">
                    <Check size={40} strokeWidth={3} />
                </div>
            </div>
            <div>
                <h3 className="text-2xl font-bold text-slate-900">{t('wizard.step_5.title')}</h3>
                <p className="text-slate-500">{t('wizard.step_5.subtitle')}</p>
            </div>

            <div className="bg-white border rounded-2xl overflow-hidden text-left shadow-sm">
                <ReviewSection title={t('wizard.step_5.section_identity')} onEdit={() => setStep(0)}>
                    <ReviewRow label={t('wizard.step_1.code')} value={formData.code} />
                    <ReviewRow label={t('wizard.step_1.description')} value={formData.description} />
                    <ReviewRow label={t('wizard.step_1.category')} value={getCategoryTitle(formData.categoryId)} />
                    <ReviewRow label={t('wizard.step_1.supplier_desc')} value={formData.supplierDescription} />
                    <ReviewRow label={t('wizard.step_1.manufacturer')} value={formData.manufacturer} />
                    <ReviewRow label={t('wizard.step_1.model')} value={formData.model} />
                    <ReviewRow label={t('wizard.step_1.serial')} value={formData.serialNumber} />
                    <ReviewRow label={t('wizard.step_1.master_serial')} value={formData.masterSerial} />
                    <ReviewRow label={t('wizard.step_1.barcode')} value={formData.barcode} />
                    <ReviewRow label={t('wizard.step_1.color', 'Color')} value={formData.color} />
                    <ReviewRow label={t('wizard.step_1.height', 'Height')} value={formData.height} />
                    <ReviewRow label={t('wizard.step_1.length', 'Length')} value={formData.length} />
                    <ReviewRow label={t('wizard.step_1.width', 'Width')} value={formData.width} />
                </ReviewSection>

                <ReviewSection title={t('wizard.step_5.section_financial')} onEdit={() => setStep(1)}>
                    <ReviewRow label={t('wizard.step_2.quantity')} value={formData.quantity} />
                    <ReviewRow label={t('wizard.step_2.unit_value')} value={formatCurrency(formData.unitValue || 0, userCurrency || 'EUR')} />
                    <ReviewRow label={t('wizard.step_2.discount')} value={`${formData.discountStats}%`} />
                    <ReviewRow label={t('wizard.step_2.total_value')} value={formatCurrency(formData.totalValue || 0, userCurrency || 'EUR')} />
                    <ReviewRow label={t('wizard.step_2.acquisition_date')} value={formData.acquisitionDate} />
                    <ReviewRow label={t('wizard.step_2.residual_value')} value={`${formData.residualValue}€`} />
                    <ReviewRow label={t('wizard.step_2.vat')} value={`${formData.vatPercent}%`} />
                    <ReviewRow label={t('wizard.step_2.supplier')} value={getSupplierName(formData.supplierId)} />
                    <ReviewRow label={t('wizard.step_2.invoice_number')} value={formData.invoiceNumber} />
                    <ReviewRow label={t('wizard.step_2.invoice_date')} value={formData.invoiceDate} />
                    <ReviewRow label={t('wizard.step_2.sent_accounting')} value={formData.sentToAccountingDate} />
                    <ReviewRow label={t('wizard.step_2.usage_start_date')} value={formData.usageStartDate} />
                </ReviewSection>

                <ReviewSection title={t('wizard.step_5.section_location')} onEdit={() => setStep(2)}>
                    <ReviewRow label={t('wizard.step_4.status')} value={t(`status.${formData.status}`)} />
                    <ReviewRow label={t('wizard.step_4.branch')} value={getBranchName(formData.branchId)} />
                    <ReviewRow label={t('wizard.step_4.cost_center')} value={getCostCenterName(formData.costCenterId)} />
                    <ReviewRow label={t('wizard.step_4.exact_location')} value={formData.exactLocation} />
                    <ReviewRow label={t('wizard.step_4.asset_manager')} value={getUserName(formData.assetManagerId)} />
                    <ReviewRow label={t('wizard.step_4.asset_manager')} value={getUserName(formData.assetManagerId)} />
                    <ReviewRow label={t('wizard.step_4.asset_user')} value={getUserName(formData.assetUserId)} />
                    <ReviewRow label={t('wizard.step_4.notes', 'Notes')} value={formData.notes} />
                </ReviewSection>
            </div>
        </div>
    );
};


function ReviewSection({ title, children, onEdit }: any) {
    return (
        <div className="border-b last:border-0">
            <div className="bg-slate-50 px-6 py-3 flex justify-between items-center">
                <h5 className="font-bold text-slate-700 uppercase text-xs tracking-wider">{title}</h5>
                <button onClick={onEdit} className="text-blue-600 text-xs font-bold hover:underline">Edit</button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
                {children}
            </div>
        </div>
    );
}

function ReviewRow({ label, value }: any) {
    return (
        <div>
            <span className="block text-xs font-bold text-slate-400 uppercase">{label}</span>
            <span className="font-medium text-slate-800">{value || '-'}</span>
        </div>
    );
}

function Field({ label, children, required }: any) {
    return (
        <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1">
                {label}
                {required && <span className="text-red-500">*</span>}
            </label>
            {children}
        </div>
    );
}
